import _ from 'lodash';
import * as React from 'react';
import { connect } from 'react-redux';
import { withTranslation, WithTranslation } from 'react-i18next';

import SearchIcon from '@material-ui/icons/Search';

import { AutocompleteAsyncInput, Suggestion } from '../AutocompleteAsyncInput';
import ConfirmCareProvider from './ConfirmCareProvider';
import { ModalInput } from '../ModalInput';
import { ItemsList, Size } from '../ItemsPage/ItemsList';

import * as api from '../../api';
import * as store from '../../reducers/store';
import * as Utils from './utils';
import * as logger from '../../logger';

import styles from './index.module.css';

///////////////////////////////////////////////////
////// CONST & TYPES

const MANUAL_CARE_PROVIDER_ID = 'manual_care_provider_id';
const EMPTY_STRING = '';

interface Props extends WithTranslation, StateProps {
  id?: string;
  initialCareProviders: api.CareProvider.CareProviderWithAddress[];
  saveFieldValue: (newValue: string) => void;
  updateFieldValue: (newValue: string) => void;
}

interface StateProps {
  authenticationCode: store.All['authenticationCode'];
}

interface State {
  isModalOpen: boolean;
  careProviders: api.CareProvider.CareProviderWithAddress[];
  manuallySelectedCareProvider: api.CareProvider.CareProviderWithAddress | null;
  currentText: string;
}

///////////////////////////////////////////////////
////// COMPONENT

class CareProviderInput extends React.Component<Props, State> {
  state: State = {
    isModalOpen: false,
    careProviders: this.props.initialCareProviders,
    manuallySelectedCareProvider: null,
    currentText: EMPTY_STRING,
  };

  setIsModalOpen = (newState: boolean) => {
    this.setState({ isModalOpen: newState });
  };

  selectProvider = (
    selectedCareProvider: api.CareProvider.CareProviderWithAddress,
  ) => {
    if (selectedCareProvider.id === MANUAL_CARE_PROVIDER_ID) {
      const displayName = [
        selectedCareProvider.firstName,
        selectedCareProvider.lastName,
      ].join(' ');
      this.setState({
        manuallySelectedCareProvider: { ...selectedCareProvider, displayName },
      });
      return;
    }

    const { saveFieldValue, updateFieldValue } = this.props;
    const { careProviders } = this.state;

    const newProviders = [...careProviders, selectedCareProvider];

    this.setState({
      careProviders: newProviders,
      currentText: EMPTY_STRING,
    });
    saveFieldValue(JSON.stringify(newProviders));
    updateFieldValue(JSON.stringify(newProviders));

    this.setIsModalOpen(false);
  };

  removeProvider = (id: string) => {
    const { saveFieldValue, updateFieldValue } = this.props;
    const { careProviders } = this.state;

    const updatedCareProviders = careProviders.filter(
      (careProvider) => careProvider.id !== id,
    );

    this.setState({
      careProviders: updatedCareProviders,
      manuallySelectedCareProvider: null,
    });

    const stringified = JSON.stringify(updatedCareProviders);

    saveFieldValue(stringified);
    updateFieldValue(stringified);
  };

  handleSelect = (
    suggestion: Suggestion<api.CareProvider.CareProviderWithAddress>,
  ) => {
    const { data } = suggestion;

    if (!data) return;

    this.selectProvider(data);
  };

  getCareProviderSuggestions = async (
    input: string,
  ): Promise<Suggestion<api.CareProvider.CareProviderWithAddress>[]> => {
    const trimmedInput = input.trim();

    if (!trimmedInput) return [];

    let suggestions: api.CareProvider.CareProviderWithAddress[];
    try {
      suggestions = await api.client.CareProvider.findSuggestions(
        trimmedInput,
        this.props.authenticationCode ?? undefined,
      );
    } catch (err) {
      logger.error(err);
      suggestions = [];
    }

    const careProviderSuggestions = suggestions.map((careProvider) => {
      return {
        primaryLabel: Utils.getPrimaryLabel(careProvider),
        secondaryLabel: Utils.getSecondaryLabels(careProvider).map(
          (label, i) => <div key={i}>{label}</div>,
        ),
        data: careProvider,
      };
    });

    const [firstName, ...rest] = trimmedInput.split(' ');

    const manualCareProviderSuggestion: api.CareProvider.CareProviderWithAddress =
      {
        id: MANUAL_CARE_PROVIDER_ID,
        displayName: `Add "${trimmedInput}"`,
        firstName: _.upperFirst(firstName.toLowerCase()),
        lastName: _.upperFirst(rest.join(' ').toLowerCase()),
        npi: '',
        address: {
          address: '',
          unitNumber: '',
          city: '',
          state: '',
          zip: '',
        },
        prefix: null,
        suffix: null,
        taxonomy: null,
        taxonomyCode: null,
      };

    return [
      ...careProviderSuggestions.slice(0, 4),
      {
        primaryLabel: manualCareProviderSuggestion.displayName,
        data: manualCareProviderSuggestion,
      },
    ];
  };

  renderChildren = () => {
    const { manuallySelectedCareProvider } = this.state;

    if (manuallySelectedCareProvider) {
      return (
        <ConfirmCareProvider
          careProvider={manuallySelectedCareProvider}
          onSave={this.selectProvider}
        />
      );
    }

    return (
      <AutocompleteAsyncInput
        onSelect={this.handleSelect}
        getSuggestions={this.getCareProviderSuggestions}
        placeholder={'e.g Alice Smith'}
        icon={<SearchIcon className={styles.searchIcon} />}
        limit={5}
        initialString={this.state.currentText}
      />
    );
  };

  // eslint-disable-next-line react/require-render-return
  render = () => {
    const { id, t } = this.props;
    const { careProviders, isModalOpen } = this.state;

    if (careProviders.length) {
      return (
        <ItemsList
          className={styles.itemsList}
          compact={false}
          items={careProviders.map((careProvider) => {
            return {
              id: careProvider.id,
              primaryLabel: Utils.getPrimaryLabel(careProvider),
              secondaryLabel: Utils.getSecondaryLabels(careProvider).map(
                (label, i) => <div key={i}>{label}</div>,
              ),
              canRemove: true,
            };
          })}
          primaryLabelSize={Size.Small}
          secondaryLabelSize={Size.Small}
          deleteItem={this.removeProvider}
        />
      );
    }

    return (
      <ModalInput
        id={id}
        children={this.renderChildren()}
        isOpen={isModalOpen}
        setIsOpen={this.setIsModalOpen}
        setCurrentText={(currentText) => this.setState({ currentText })}
        placeholder={t('Enter their name...')}
        startIcon={<SearchIcon className={styles.searchIcon} />}
        initialValue={EMPTY_STRING}
        onModalClose={() => {
          this.setIsModalOpen(false);
          this.setState({
            manuallySelectedCareProvider: null,
            currentText: EMPTY_STRING,
          });
        }}
      />
    );
  };
}

const TranslatedCareInput = withTranslation()(CareProviderInput);
export default connect(
  (store: store.All): StateProps => ({
    authenticationCode: store.authenticationCode,
  }),
)(TranslatedCareInput);
